/** Hạng thành viên theo tổng chi tiêu (#18734). Pro-only — backend trả 402 trên gói Lite. */
import { apiClient } from "./client";

export interface MembershipTier {
  id: string;
  code: string;
  name: string;
  minSpendVnd: number;
  rankOrder: number;
  active: boolean;
  /**
   * Khi nào phòng khám đặt/xác nhận ngưỡng này. `null` = ngưỡng gợi ý do migration seed, chưa
   * phải chính sách của phòng khám (#18871) — bệnh nhân chưa nhìn thấy hạng.
   */
  confirmedAt: string | null;
}

export interface Membership {
  /** Hoá đơn + chi tiêu chuyển đổi. Đây là số dùng để xét hạng. */
  totalSpentVnd: number;
  /** Đã trả tại Addy HIS. */
  invoiceSpentVnd: number;
  /**
   * Chuyển đổi từ phần mềm cũ hoặc cấp tay. Tách riêng có chủ ý: hạng kiếm bằng hoá đơn thật
   * và hạng do nhập tay không nên trông giống hệt nhau.
   */
  adjustedSpentVnd: number;
  tierCode: string | null;
  tierName: string | null;
  rankOrder: number;
  nextTierCode: string | null;
  nextTierName: string | null;
  nextTierMinSpendVnd: number | null;
  spendToNextTierVnd: number | null;
  /**
   * `true` khi bậc thang vẫn là ngưỡng gợi ý chưa ai xác nhận (#18871). Màn hình nhân viên vẫn
   * hiện hạng kèm cảnh báo; API của bệnh nhân (`/patients/me/membership`) không trả hạng.
   */
  provisionalThresholds: boolean;
}

export type SpendAdjustmentKind = "MIGRATION" | "CORRECTION" | "MANUAL";

export interface SpendAdjustment {
  id: string;
  amountVnd: number;
  kind: SpendAdjustmentKind;
  reason: string;
  createdAt: string;
  createdBy: string | null;
}

export interface TierForm {
  code: string;
  name: string;
  minSpendVnd: number;
  rankOrder: number;
  active?: boolean;
}

export const membershipQueryKeys = {
  tiers: () => ["membership", "tiers"] as const,
  ofPatient: (patientId: string) => ["membership", "patient", patientId] as const,
  adjustments: (patientId: string) => ["membership", "adjustments", patientId] as const,
};

export const membershipApi = {
  tiers: () => apiClient.get<MembershipTier[]>("/v1/membership/tiers").then((r) => r.data),

  createTier: (form: TierForm) =>
    apiClient.post<{ id: string }>("/v1/membership/tiers", form).then((r) => r.data),

  /** `code` is ignored by the API — a tier code cannot change once patients have been told it. */
  updateTier: (id: string, form: TierForm) =>
    apiClient.put<void>(`/v1/membership/tiers/${id}`, form).then(() => undefined),

  /** "Ngưỡng gợi ý này dùng được" — nhận bậc thang seed làm chính sách của phòng khám (#18871). */
  confirmTiers: () =>
    apiClient
      .post<{ confirmed: number }>("/v1/membership/tiers/confirm")
      .then((r) => r.data.confirmed),

  /** Deactivates; the backend never deletes a tier. */
  deactivateTier: (id: string) =>
    apiClient.delete<void>(`/v1/membership/tiers/${id}`).then(() => undefined),

  ofPatient: (patientId: string) =>
    apiClient.get<Membership>(`/v1/patients/${patientId}/membership`).then((r) => r.data),

  adjustments: (patientId: string) =>
    apiClient
      .get<SpendAdjustment[]>(`/v1/patients/${patientId}/membership/adjustments`)
      .then((r) => r.data),

  adjust: (patientId: string, amountVnd: number, kind: SpendAdjustmentKind, reason: string) =>
    apiClient
      .post<{ id: string }>(`/v1/patients/${patientId}/membership/adjustments`, {
        amountVnd,
        kind,
        reason,
      })
      .then((r) => r.data),
};

export const ADJUSTMENT_KIND_LABELS: Record<SpendAdjustmentKind, string> = {
  MIGRATION: "Chuyển từ phần mềm cũ",
  CORRECTION: "Sửa sai",
  MANUAL: "Cấp tay",
};
